import React, { useState, useEffect } from "react";
import { useAccount, usePublicClient } from "wagmi";
import { namehash } from "viem/ens";

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute("content") : "";
}

export default function CollectionSetup() {
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient();
  const [ensName, setEnsName] = useState("");
  const [loading, setLoading] = useState(false);
  const [nftContractAddress, setNftContractAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!address || !publicClient) return;

    setLoading(true);
    publicClient
      .getEnsName({ address })
      .then((name) => {
        if (name) setEnsName(name);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [address, publicClient]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!ensName) {
      setError("Please select or enter an ENS name.");
      return;
    }

    if (!/^0x[0-9a-fA-F]{40}$/.test(nftContractAddress)) {
      setError("Please enter a valid NFT contract address.");
      return;
    }

    const parentNode = namehash(ensName);

    setSubmitting(true);
    try {
      const response = await fetch("/collections", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({
          collection: {
            ens_name: ensName,
            name: ensName.replace(/\.eth$/, ""),
            nft_contract_address: nftContractAddress,
            chain_id: "1",
            parent_node: parentNode,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.redirect_to;
      } else {
        const data = await response.json();
        setError(
          data.errors ? data.errors.join(", ") : "Failed to create collection."
        );
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400 text-lg">
          Connect your wallet to set up a collection.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
        Set up your collection
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            ENS Name
          </label>

          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Looking up your ENS name...
            </p>
          ) : (
            <input
              type="text"
              value={ensName}
              onChange={(e) => setEnsName(e.target.value)}
              placeholder="e.g. nouns.eth"
              className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            NFT Contract Address
          </label>
          <input
            type="text"
            value={nftContractAddress}
            onChange={(e) => setNftContractAddress(e.target.value)}
            placeholder="0x..."
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-4 py-2 text-sm font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Creating..." : "Create Collection"}
          </button>
        </div>
      </form>
    </div>
  );
}
